import { useEffect, useState } from "react";
import { ActionIcon, Box, Button, createStyles, Drawer, Select, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconFileText, IconUsers, IconX } from "@tabler/icons-react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import ServiceRequestFields, {
  buildConditionContext,
  ClientsField,
  conditionHolds,
  DateField,
  DescriptionField,
  FacilityField,
  IssueCategoryField,
  PhotosField,
  PriorityField,
  ShiftField,
  srCaretProps,
  StaffField,
  TimeField,
  TitleField,
  useScopedFacilities,
  useSrButtonClassNames,
  useSrFieldClassNames,
} from "../ServiceRequestFields";
import { templateFormCards } from "../ServiceRequestTemplateBuilder/schema";
import { createServiceRequest, getTemplates } from "../../services/serviceRequests";

const isFilled = (v) => !(v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0));

// A request is nearly always filed for right now, so the drawer opens on
// today's date and the current time rather than making the submitter enter
// what the clock already knows.
const blankValues = () => ({
  title: "",
  priority: "Medium",
  issueCategory: "",
  facility: "",
  clients: [],
  staff: [],
  shift: "",
  date: new Date(),
  time: dayjs().format("HH:mm"),
  description: "",
  photos: [],
});

// Date/Time fields a request type brings along open on today/now for exactly
// the reason the built-in pair does — a request type that defines its own form
// shouldn't ask the submitter to key in what the clock already knows. Seeded in
// the shape each control round-trips: DateInput reads a Date, TimeInput a
// 24h "HH:mm" string.
const templateFieldDefaults = (template) => Object.fromEntries(
  (template?.fields || [])
    .filter((f) => f.fieldType === "Date" || f.fieldType === "Time")
    .map((f) => [f.fieldId, f.fieldType === "Date" ? new Date() : dayjs().format("HH:mm")])
);

// ServiceRequest.date/.time are plain strings on the model, and every consumer
// (reports, request entries) prints them verbatim — so store the same human
// format the fields display instead of an ISO timestamp / 24h clock.
const dateToPayload = (d) => (d ? dayjs(d).format("MMM D, YYYY") : undefined);
const timeToPayload = (t) => {
  if (!t) return undefined;
  const parsed = dayjs(`2000-01-01T${t}`);
  return parsed.isValid() ? parsed.format("h:mm A") : t;
};

// Mirrors the backend's own validateRequiredFields (serviceRequest.service.js)
// so the drawer can block submit and point at what's missing instead of
// round-tripping to find out. Calculation fields are server-evaluated, never
// user-entered, so they're exempt even when marked required.
//
// The built-ins carrying a required marker in the mockup (Title, Priority,
// Issue Category, Description) are enforced alongside them — the asterisk has
// to mean something. Priority always carries a default, so it can't be empty.
// Facility is NOT in this list — see the facilityValue param below.
const CORE_REQUIRED = [
  ["title", "Title"],
  ["issueCategory", "Issue Category"],
  ["description", "Description"],
];

// `withCore` is false once a custom template supplies the whole form — the
// built-in attributes aren't on screen to fill in, so demanding them would
// deadlock submit. Facility is exempt from that split: it's a real
// ServiceRequest field with no template-level fallback, so it's always
// required — but *where* its value comes from (the built-in field, or a
// template field of type "Facility") varies, so the caller resolves the
// actual value and passes it in rather than this function assuming
// `values.facility`.
const missingRequiredFields = (template, values, fieldValues, withCore, facilityValue) => [
  ...(!isFilled(facilityValue) ? ["Facility"] : []),
  ...(withCore
    ? CORE_REQUIRED.filter(([key]) => !isFilled(typeof values[key] === "string" ? values[key].trim() : values[key])).map(([, label]) => label)
    : []),
  ...(template?.fields || [])
    .filter((f) => f.required && f.fieldType !== "Calculation")
    .filter((f) => !isFilled(fieldValues[f.fieldId]))
    .map((f) => f.label),
];

const useStyles = createStyles(() => ({
  // Full-bleed on a phone, so the rounded left edge (which reads as "there's
  // page behind me") goes away along with the sliver of overlay beside it.
  drawerContent: {
    borderRadius: "16px 0 0 16px",
    "@media (max-width: 768px)": { borderRadius: 0 },
  },
  root: { display: "flex", flexDirection: "column", height: "100%", minHeight: 0, background: "var(--app-bg)" },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
    padding: "17px 22px", borderBottom: "1px solid var(--app-border)", flexShrink: 0, background: "var(--app-surface)",
    "@media (max-width: 768px)": { padding: "14px 16px" },
  },
  title: { fontSize: 21, fontWeight: 700, color: "var(--app-text)", letterSpacing: "-0.015em", "@media (max-width: 768px)": { fontSize: 18 } },
  closeBtn: { width: 34, height: 34, borderRadius: 9, background: "transparent", color: "var(--app-text-muted)", flexShrink: 0, "&:hover": { background: "var(--app-surface-2)" } },

  body: { flex: 1, overflowY: "auto", padding: 20, "@media (max-width: 768px)": { padding: 12 } },
  card: {
    background: "var(--app-surface)", border: "1px solid var(--app-border)", borderRadius: 16,
    padding: 18, marginBottom: 14,
    "@media (max-width: 768px)": { padding: 14, borderRadius: 12, marginBottom: 12 },
  },
  // Matches the mockup's `.dcard` wrapping just the Request Type field —
  // it uses tighter padding (15px 16px) than the other cards (18px).
  cardTight: {
    background: "var(--app-surface)", border: "1px solid var(--app-border)", borderRadius: 16,
    padding: "15px 16px", marginBottom: 14,
  },
  // Mockup `#drawer-new .dcard .ch2`: 12px/600 uppercase, muted icon.
  cardHead: {
    display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 600,
    textTransform: "uppercase", letterSpacing: ".04em", color: "var(--app-text)", marginBottom: 14,
    "& svg": { color: "var(--app-text-muted)" },
  },
  emptyHint: { textAlign: "center", marginTop: 8 },

  // Matches the mockup's #drawer-new .dgrid — a 12-col grid so paired fields
  // (Priority/Issue Category, etc.) sit side by side instead of stacking.
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(12, 1fr)",
    gap: "0 14px",
    alignItems: "start",
  },
  span12: { gridColumn: "span 12" },
  // Half-width pairs (Facility/Shift, Date/Time, Priority/Category) hold up
  // fine on a tablet, but at phone width each half is ~150px — narrower than
  // the date and select controls inside them — so they go full width.
  span6: { gridColumn: "span 6", "@media (max-width: 640px)": { gridColumn: "span 12" } },

  footer: {
    display: "flex", gap: 12, padding: "16px 20px",
    borderTop: "1px solid var(--app-border)", background: "var(--app-surface)", flexShrink: 0,
    "@media (max-width: 768px)": { padding: "12px 14px", gap: 10 },
  },
  footerBtn: { flex: 1, height: "auto", padding: 13, borderRadius: 9, justifyContent: "center" },
  primaryBtn: {
    flex: 1,
    height: "auto",
    padding: 13,
    borderRadius: 9,
    backgroundColor: "var(--app-primary)",
    borderColor: "var(--app-primary)",
    color: "var(--app-on-primary)",
    "&:hover": { backgroundColor: "var(--app-primary) !important", opacity: 0.85 },
  },
}));

const ServiceRequestNewDrawer = ({ opened, close, onCreated }) => {
  const { classes } = useStyles();
  // A form drawer this dense has no business leaving a strip of dimmed page
  // beside it on a phone — it takes the whole viewport instead.
  const isMobile = useMediaQuery("(max-width: 768px)", undefined, { getInitialValueInEffect: false });
  const fieldClassNames = useSrFieldClassNames();
  const btnClass = useSrButtonClassNames();
  const { templates, issueCategories } = useSelector((state) => state.serviceRequests);
  const me = useSelector((state) => state.profile?.profile?.user);
  const myId = String(me?.id || me?._id || "");
  const [templateId, setTemplateId] = useState("");
  const [values, setValues] = useState(blankValues);
  const [fieldValues, setFieldValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);

  const template = templates.find((t) => t.id === templateId);

  // A custom request type describes its whole form: the fields its author
  // placed on the builder canvas are exactly what the submitter fills in, in
  // exactly that layout — so the built-in Details/Request cards step aside
  // rather than sitting above it collecting attributes nobody asked for.
  //
  // The four global default types keep them: they define no form of their own
  // and need the submitter to name a facility directly. A custom type whose
  // builder is still empty keeps them too — otherwise picking it would open a
  // drawer with nothing in it.
  const templateDefinesForm = !!template && !template.isDefault && template.fields?.length > 0;
  const conditionCtx = buildConditionContext({
    sections: template?.sections, formFields: template?.fields, values: fieldValues,
  });
  const formCards = templateFormCards(template).filter((card) => conditionHolds(card.cond, conditionCtx));

  // A field only "belongs" to the template if the submitter can actually
  // see and fill it in: its own section (if any) has to pass the same
  // `cond` check formCards just filtered sections by, and the field's own
  // `conditional` (evaluated per-field in ServiceRequestFields) has to hold
  // too. A field that merely exists in template.fields but sits behind an
  // unmet condition is invisible on the real form — treating it as "handled"
  // would hide the shared fallback below it and leave the value uncollectable.
  const sectionsById = new Map((template?.sections || []).map((s) => [s.sectionId, s]));
  const templateFieldReachable = (field) => {
    if (!field) return false;
    const section = field.sectionId ? sectionsById.get(field.sectionId) : null;
    if (section && !conditionHolds(section.cond, conditionCtx)) return false;
    return conditionHolds(field.conditional, conditionCtx);
  };

  // Facility is always required, but where it's collected from depends on
  // the template: one that defines its own form may already ask for it as
  // one of its own fields (fieldType "Facility") — if so, that answer IS the
  // request's facility, and the separate built-in field stays hidden so the
  // submitter isn't asked for it twice. Either way the resolved value is
  // what actually goes on the wire as the request's top-level `facility`.
  const rawTemplateFacilityField = (template?.fields || []).find((f) => f.fieldType === "Facility") || null;
  const templateFacilityFieldId = templateFieldReachable(rawTemplateFacilityField) ? rawTemplateFacilityField.fieldId : null;
  const resolvedFacility = templateFacilityFieldId ? fieldValues[templateFacilityFieldId] : values.facility;
  const missingRequired = missingRequiredFields(template, values, fieldValues, !templateDefinesForm, resolvedFacility);

  // Same reasoning as Facility above: a template that defines its own
  // Client/Staff-type field is the only place that answer is collected, so
  // it has to ALSO ride along as the request's real top-level
  // clients/staff — every other consumer (reports, filters, the detail
  // drawer's own built-in Client(s)/Staff rows) reads those top-level
  // fields directly and has no reason to know a custom field even exists.
  const rawTemplateClientField = (template?.fields || []).find((f) => f.fieldType === "Client") || null;
  const rawTemplateStaffField = (template?.fields || []).find((f) => f.fieldType === "Staff") || null;
  const templateClientFieldId = templateFieldReachable(rawTemplateClientField) ? rawTemplateClientField.fieldId : null;
  const templateStaffFieldId = templateFieldReachable(rawTemplateStaffField) ? rawTemplateStaffField.fieldId : null;
  const asIdArray = (v) => (Array.isArray(v) ? v : [v]).filter(Boolean);
  const resolvedClients = templateClientFieldId ? asIdArray(fieldValues[templateClientFieldId]) : values.clients;
  const resolvedStaff = templateStaffFieldId ? asIdArray(fieldValues[templateStaffFieldId]) : values.staff;

  // A conditional section can turn reachable or unreachable mid-fill (its
  // `cond` depends on some other answer), which flips which control is "live"
  // for Facility/Client/Staff — the built-in fallback field, or the template's
  // own field of that type (see templateFieldReachable above). Without this,
  // an answer already given on one side goes silently unread once the other
  // side becomes live, and the submitter sees "Required before submitting"
  // for something they already filled in. Carrying the value across on every
  // reachability flip keeps whichever side is live pre-filled with it.
  useEffect(() => {
    const facilityFieldId = rawTemplateFacilityField?.fieldId || null;
    if (!opened || !facilityFieldId) return;
    const fromField = fieldValues[facilityFieldId];
    const fromCore = values.facility;
    if (!isFilled(fromField) && isFilled(fromCore)) {
      setFieldValues((v) => ({ ...v, [facilityFieldId]: fromCore }));
    } else if (!isFilled(fromCore) && isFilled(fromField)) {
      setValues((v) => ({ ...v, facility: fromField }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, rawTemplateFacilityField?.fieldId, templateFacilityFieldId]);

  useEffect(() => {
    const clientFieldId = rawTemplateClientField?.fieldId || null;
    if (!opened || !clientFieldId) return;
    const fromField = asIdArray(fieldValues[clientFieldId]);
    const fromCore = values.clients;
    if (!fromField.length && fromCore.length) {
      setFieldValues((v) => ({ ...v, [clientFieldId]: fromCore }));
    } else if (!fromCore.length && fromField.length) {
      setValues((v) => ({ ...v, clients: fromField }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, rawTemplateClientField?.fieldId, templateClientFieldId]);

  useEffect(() => {
    const staffFieldId = rawTemplateStaffField?.fieldId || null;
    if (!opened || !staffFieldId) return;
    const fromField = asIdArray(fieldValues[staffFieldId]);
    const fromCore = values.staff;
    if (!fromField.length && fromCore.length) {
      setFieldValues((v) => ({ ...v, [staffFieldId]: fromCore }));
    } else if (!fromCore.length && fromField.length) {
      setValues((v) => ({ ...v, staff: fromField }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, rawTemplateStaffField?.fieldId, templateStaffFieldId]);

  // Facility follows the clients on the request: pick a client and only the
  // facilities that serve them stay selectable.
  const { options: facilityOptions } = useScopedFacilities(values.clients);
  const facilityIdsKey = facilityOptions.map((o) => o.value).join(",");

  useEffect(() => {
    if (opened) {
      setTemplateId("");
      // The submitter is on their own request by default — they're the one who
      // saw the problem, so they're pre-filled into Staff and saved with it.
      setValues({ ...blankValues(), staff: myId ? [myId] : [] });
      setFieldValues({});
      setTried(false);
      // Load the selectable request types (the org's own templates + the 4 global
      // defaults) so the Request Type dropdown is always populated on open.
      getTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  // Picking a request type starts its form fresh: the previous type's answers
  // are keyed to fields this form doesn't have, and this one's Date/Time fields
  // open on now. Keyed on templateId rather than the template object so a
  // background getTemplates() refresh can't wipe what's been typed in.
  useEffect(() => {
    if (!opened) return;
    setFieldValues(templateFieldDefaults(template));
    setTried(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, templateId]);

  // Keep Facility honest as the clients change: drop a choice the new client
  // set doesn't serve, and fill it in only when the scoping leaves exactly one
  // possibility. Nothing else is auto-picked — the submitter's home board has
  // a facility, but that's where they happen to be standing, not a statement
  // about where this request belongs. Only relevant for the built-in field —
  // a template's own Facility-type field manages its own value independently.
  useEffect(() => {
    if (!opened || templateFacilityFieldId) return;
    const ids = facilityIdsKey ? facilityIdsKey.split(",") : [];
    if (values.facility && ids.includes(values.facility)) return;
    const next = ids.length === 1 ? ids[0] : "";
    if (next !== values.facility) setValues((v) => ({ ...v, facility: next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, facilityIdsKey, values.facility, templateFacilityFieldId]);

  const onCoreChange = (key, val) => setValues((v) => ({ ...v, [key]: val }));
  const onFieldChange = (fieldId, val) => setFieldValues((v) => ({ ...v, [fieldId]: val }));

  // Submit spins the button for the whole round trip, then hands the created
  // request up so the list/board behind the drawer shows it right away. The
  // drawer only closes on success — on failure the error toast lands over a
  // still-filled form instead of a form the submitter has to retype.
  //
  // createServiceRequest resolves to the created request on success or null
  // on failure (it shows the error itself either way) — awaiting that return
  // value, rather than only reacting to a success-only callback, is what
  // lets `saving` clear on BOTH outcomes. Relying on the callback alone left
  // it stuck true forever after a failed submit: Submit's spinner never
  // stopped and Cancel (disabled while saving) never re-enabled, so the only
  // way out was closing the drawer with X or reloading the page.
  const onSave = async () => {
    if (saving) return;
    if (!templateId || missingRequired.length > 0) {
      setTried(true);
      return;
    }
    setSaving(true);
    // Only the selected request type's own fields go up: anything keyed to a
    // type that was picked and then swapped out is stale, and Calculation
    // fields are recomputed server-side.
    const payloadFieldValues = Object.fromEntries(
      Object.entries(fieldValues).filter(([fieldId]) => {
        const f = template?.fields?.find((ff) => ff.fieldId === fieldId);
        return !!f && f.fieldType !== "Calculation";
      })
    );
    const created = await createServiceRequest(
      templateDefinesForm
        // The built-in cards were never shown, so none of their values are
        // sent — except Facility, which always rides along as the request's
        // own top-level field (resolvedFacility above), whether it came from
        // the template's own Facility-type field or (if it has none) the
        // small standalone Facility field shown above the template's cards.
        ? { templateId, facility: resolvedFacility || undefined, clients: resolvedClients, staff: resolvedStaff, fieldValues: payloadFieldValues }
        : {
          templateId,
          // A template has no facility of its own — every request always
          // carries the submitter's own explicit choice.
          facility: values.facility || undefined,
          title: values.title.trim(),
          priority: values.priority,
          issueCategory: values.issueCategory || undefined,
          clients: values.clients,
          staff: values.staff,
          shift: values.shift || undefined,
          date: dateToPayload(values.date),
          time: timeToPayload(values.time),
          description: values.description || undefined,
          photos: values.photos,
          fieldValues: payloadFieldValues,
        }
    );
    setSaving(false);
    if (created) {
      onCreated?.();
      close();
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={close}
      position="right"
      size={isMobile ? "100%" : "clamp(420px, 52vw, 720px)"}
      withCloseButton={false}
      padding={0}
      classNames={{ content: classes.drawerContent }}
      overlayProps={{ opacity: 0.55, blur: 3 }}
      zIndex={1000}
    >
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Text className={classes.title}>New Service Request</Text>
          <ActionIcon className={classes.closeBtn} onClick={close} aria-label="Close"><IconX size={15} /></ActionIcon>
        </Box>

        <Box className={classes.body}>
          <Box className={classes.cardTight}>
            <Select
              label="Request Type"
              required
              classNames={fieldClassNames}
              placeholder="Select a request type…"
              data={templates.filter((t) => t.active !== false).map((t) => ({ value: t.id, label: t.name }))}
              value={templateId}
              onChange={setTemplateId}
              {...srCaretProps()}
            />
          </Box>

          {/* Built-in ServiceRequest attributes. Shift/Date/Time are real
              top-level model fields (serviceRequest.model.js). They stand in
              for a form only where the request type doesn't bring one of its
              own — see templateDefinesForm.

              Nothing below Request Type exists until a type is picked: which
              form the drawer shows *is* the answer to that question, so an
              unanswered drawer is just the one field. */}
          {!!template && !templateDefinesForm && (
            <>
              <Box className={classes.card}>
                <Box className={classes.cardHead}><IconUsers size={15} />Details</Box>
                <Box className={classes.grid}>
                  <Box className={classes.span12}>
                    <StaffField value={values.staff} onChange={(v) => onCoreChange("staff", v)} />
                  </Box>
                  <Box className={classes.span12}>
                    <ClientsField value={values.clients} onChange={(v) => onCoreChange("clients", v)} />
                  </Box>
                  {/* Facility sits under Client, as in the design: the clients
                      on the request are what narrow the facilities on offer
                      (useScopedFacilities), so the two read in that order.
                      Hidden only when the request type collects it itself. */}
                  {!templateFacilityFieldId && (
                    <Box className={classes.span6}>
                      <FacilityField value={values.facility} onChange={(v) => onCoreChange("facility", v)} clientIds={values.clients} />
                    </Box>
                  )}
                  <Box className={classes.span6}>
                    <ShiftField value={values.shift} onChange={(v) => onCoreChange("shift", v)} />
                  </Box>
                  <Box className={classes.span6}>
                    <DateField value={values.date} onChange={(v) => onCoreChange("date", v)} />
                  </Box>
                  <Box className={classes.span6}>
                    <TimeField value={values.time} onChange={(v) => onCoreChange("time", v)} />
                  </Box>
                </Box>
              </Box>

              <Box className={classes.card}>
                <Box className={classes.cardHead}><IconFileText size={15} />Request</Box>
                <Box className={classes.grid}>
                  <Box className={classes.span12}>
                    <TitleField value={values.title} onChange={(v) => onCoreChange("title", v)} />
                  </Box>
                  <Box className={classes.span6}>
                    <PriorityField value={values.priority} onChange={(v) => onCoreChange("priority", v)} />
                  </Box>
                  <Box className={classes.span6}>
                    <IssueCategoryField value={values.issueCategory} onChange={(v) => onCoreChange("issueCategory", v)} issueCategories={issueCategories} />
                  </Box>
                  <Box className={classes.span12}>
                    <DescriptionField value={values.description} onChange={(v) => onCoreChange("description", v)} />
                  </Box>
                  <Box className={classes.span12}>
                    <PhotosField value={values.photos} onChange={(v) => onCoreChange("photos", v)} />
                  </Box>
                </Box>
              </Box>
            </>
          )}

          {/* Facility has no template-level fallback, so a custom request
              type that defines its own form still needs somewhere to set it
              when its own canvas doesn't already collect it as a Facility-
              type field — otherwise it's required forever with no control
              to fill it. */}
          {!!template && templateDefinesForm && !templateFacilityFieldId && (
            <Box className={classes.card}>
              <Box className={classes.cardHead}><IconUsers size={15} />Details</Box>
              <Box className={classes.grid}>
                <Box className={classes.span6}>
                  <FacilityField value={values.facility} onChange={(v) => onCoreChange("facility", v)} clientIds={resolvedClients} />
                </Box>
              </Box>
            </Box>
          )}

          {/* One card per section the request type was built in, in canvas
              order — see templateFormCards. */}
          {formCards.map((card) => (
            <Box key={card.id} className={classes.card}>
              <Box className={classes.cardHead}><IconFileText size={15} />{card.title}</Box>
              <Box className={classes.grid}>
                <ServiceRequestFields
                  fields={card.fields}
                  allFields={template.fields}
                  sections={template.sections}
                  values={fieldValues}
                  onChange={onFieldChange}
                />
              </Box>
            </Box>
          ))}

          {!template && (
            <Text fz={12.5} c="var(--app-text-muted)" className={classes.emptyHint}>
              Select a request type above to see its fields.
            </Text>
          )}

          {/* Like the mockup, Submit stays live and validates on press — the
              missing list only appears once the submitter has actually tried. */}
          {tried && missingRequired.length > 0 && (
            <Text fz={12.5} c="var(--app-ink-red)" mt={4}>
              Required before submitting: {missingRequired.join(", ")}
            </Text>
          )}
        </Box>

        {/* Same rule as the form above: with no request type picked there's
            nothing to submit or cancel out of, so the footer stays away until
            there is. The header's × still closes an untouched drawer. */}
        {!!template && (
          <Box className={classes.footer}>
            <Button variant="default" disabled={saving} className={`${btnClass} ${classes.footerBtn}`} onClick={close}>Cancel</Button>
            <Button variant="filled" loading={saving} onClick={onSave} className={classes.primaryBtn}>
              Submit Request
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default ServiceRequestNewDrawer;
